/**
 * @fileoverview `useFormatUsd` — a locale-bound {@link formatUsd} for components.
 * @package @puffin-core/money
 * @module lib/esm/useFormatUsd
 * @copyright ParcelPuffin 2026
 */

import { useIntlLocale } from '@puffin-core/i18n-bootstrap';
import { useCallback } from 'react';

import {
  formatUsd,
  formatUsdOrAbsent,
  formatUsdWhole,
  formatUsdWholeOrAbsent,
  formatUsdFromCents,
} from './formatUsd.js';

/**
 * A locale-bound {@link formatUsd} for use inside a component.
 *
 * ES-I18N-FOLLOW-ON Q-11. The files this replaced declared a module-level
 * `formatCurrency(amount)` and then called it four to nine times each, often from
 * several sub-components in the same file — roughly **150 call sites** in pos-web alone.
 * Threading a `locale` argument through every one of them would have been enormous churn
 * for zero readability gain, and 150 chances to miss one.
 *
 * This hook binds the locale once per component instead, so the call sites keep their
 * original single-argument shape:
 *
 * ```js
 * const formatCurrency = useFormatUsd();
 * // …unchanged: formatCurrency(total)
 * ```
 *
 * @returns {(amount: number) => string} A stable, locale-bound formatter. The identity is
 *   stable across renders at a fixed locale and is rebuilt when the language changes —
 *   which matters, because under the ecosystem's `es-US` mapping money output is
 *   byte-identical across languages, so a stale binding would LOOK correct today and be
 *   wrong the moment a locale whose money differs is introduced.
 */
export function useFormatUsd() {
  const intlLocale = useIntlLocale();
  return useCallback((amount) => formatUsd(amount, intlLocale), [intlLocale]);
}

/**
 * A locale-bound {@link formatUsdOrAbsent} for use inside a component.
 *
 * Same contract as {@link useFormatUsd}, for a value that may not be readable (a nullable column, PostgreSQL's numeric
 * string): pass the RAW value, never `Number(value)` — `Number(null)` is 0, so the parse turns "unknown" into "$0.00"
 * before the formatter can see it (ZERO-DEFAULT MONEY, 2026-10-19).
 *
 * @returns {(value: *) => string} A stable, locale-bound formatter that renders the absent mark for an unreadable value.
 */
export function useFormatUsdOrAbsent() {
  const intlLocale = useIntlLocale();
  return useCallback((value) => formatUsdOrAbsent(value, intlLocale), [intlLocale]);
}

/**
 * A locale-bound {@link formatUsdWhole} for use inside a component.
 *
 * Same contract as {@link useFormatUsd}, for the surfaces that render money without
 * cents — KPI hero strips and variance deltas.
 *
 * @returns {(amount: number) => string} A stable, locale-bound whole-dollar formatter.
 */
export function useFormatUsdWhole() {
  const intlLocale = useIntlLocale();
  return useCallback((amount) => formatUsdWhole(amount, intlLocale), [intlLocale]);
}

/**
 * A locale-bound {@link formatUsdWholeOrAbsent} for use inside a component: the whole-dollar surfaces whose value may
 * not be readable (a planner grid cell, a tier row's prior-year leg). Pass the RAW value.
 *
 * @returns {(value: *) => string} A stable, locale-bound whole-dollar formatter that renders the absent mark for an
 *   unreadable value.
 */
export function useFormatUsdWholeOrAbsent() {
  const intlLocale = useIntlLocale();
  return useCallback((value) => formatUsdWholeOrAbsent(value, intlLocale), [intlLocale]);
}

/**
 * A locale-bound {@link formatUsdFromCents} for use inside a component.
 *
 * Same contract as {@link useFormatUsd}, for the many surfaces whose API returns
 * integer cents.
 *
 * @returns {(cents: number) => string} A stable, locale-bound cents formatter.
 */
export function useFormatUsdFromCents() {
  const intlLocale = useIntlLocale();
  return useCallback((cents) => formatUsdFromCents(cents, intlLocale), [intlLocale]);
}

export default useFormatUsd;
